import { useCallback, useMemo, useRef, useState } from 'react'
import type { Key } from 'react'
import { usePaymentBotRecentFailuresQuery } from '../../../../../api/payment-bot-dashboard-api-client'
import type { ProcessTimeBucket } from '../../../../../api/dto/get-payment-bot-dashboard.dto'
import { ALL_CARRIERS } from '../../../shared/constants'
import { parseProcessTimeBucket } from '../../../shared/utils'
import type {
  FailureSortColumn,
  CarrierIdSelection,
} from '../../../shared/types'

interface Args {
  carrierId: CarrierIdSelection
  month: number
  year: number
  /** Resolved name for a numeric carrierId; null until resolved / for 'all'. */
  resolvedCarrierName: string | null
}

export function useCarrierFailures({
  carrierId,
  month,
  year,
  resolvedCarrierName,
}: Args) {
  const [selectedErrorCode, setSelectedErrorCode] = useState<string | null>(
    null
  )
  const [failurePage, setFailurePage] = useState(1)
  const [failurePageSize, setFailurePageSize] = useState(50)
  const [tableSorter, setTableSorter] = useState<{
    columnKey: FailureSortColumn
    order: 'ascend' | 'descend'
  } | null>(null)
  const [selectedCarrierNames, setSelectedCarrierNames] = useState<string[]>([])
  const [processTimeBucket, setProcessTimeBucket] =
    useState<ProcessTimeBucket | null>(null)
  const [secondAttemptSuccess, setSecondAttemptSuccess] = useState<
    boolean | null
  >(null)
  const [selectedBenefitId, setSelectedBenefitId] = useState<string | null>(
    null
  )

  const isAllCarriers = carrierId === ALL_CARRIERS

  // The recent-failures table starts unfiltered (mixed error codes) and only
  // narrows to a single code when the user clicks an Error Breakdown bar. We deliberately
  // do NOT auto-apply the top error code, so a period/carrier change must reset
  // the filter back to "show everything".
  const periodKey = `${carrierId}-${month}-${year}`
  const settledPeriodRef = useRef(periodKey)
  const periodChanged = settledPeriodRef.current !== periodKey
  if (periodChanged) {
    settledPeriodRef.current = periodKey
    setSelectedErrorCode(null)
    setFailurePage(1)
    setFailurePageSize(50)
    setSelectedCarrierNames([])
    setTableSorter(null)
    setProcessTimeBucket(null)
    setSecondAttemptSuccess(null)
    setSelectedBenefitId(null)
  }

  const failureCarrierNames = useMemo(() => {
    if (carrierId !== ALL_CARRIERS) {
      return resolvedCarrierName ? [resolvedCarrierName] : undefined
    }
    return selectedCarrierNames.length > 0 ? selectedCarrierNames : undefined
  }, [carrierId, resolvedCarrierName, selectedCarrierNames])

  // Don't fire while a numeric carrierId hasn't resolved to a name yet —
  // an undefined carrierNames would silently hit the all-carriers aggregate
  // endpoint instead of the selected carrier.
  const isCarrierResolved =
    carrierId === ALL_CARRIERS || resolvedCarrierName !== null

  const {
    data: failuresData,
    isFetching: failuresFetching,
    isError: failuresIsError,
    error: failuresError,
  } = usePaymentBotRecentFailuresQuery(
    {
      month,
      year,
      errorCode: selectedErrorCode ?? undefined,
      page: failurePage,
      limit: failurePageSize,
      carrierNames: failureCarrierNames,
      ...(tableSorter
        ? {
            sortBy: tableSorter.columnKey,
            sortOrder: tableSorter.order === 'ascend' ? 'asc' : 'desc',
          }
        : {}),
      ...(processTimeBucket ? { processTimeBucket } : {}),
      ...(secondAttemptSuccess !== null ? { secondAttemptSuccess } : {}),
      ...(selectedBenefitId ? { benefitId: selectedBenefitId } : {}),
    },
    {
      enabled: !periodChanged && isCarrierResolved,
    }
  )

  // On error, suppress the last cached success so operators never see stale
  // rows that no longer match the active filters/period alongside the error.
  const recentFailures = failuresIsError
    ? []
    : (failuresData?.recentFailures ?? [])
  const failuresTotal = failuresIsError ? 0 : (failuresData?.total ?? 0)

  const handleFailureTableChange = useCallback(
    (
      _: unknown,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter: unknown,
      extra: { action: 'paginate' | 'sort' | 'filter' }
    ) => {
      if (extra.action === 'paginate') return

      const s = Array.isArray(sorter) ? sorter[0] : sorter
      const columnKey =
        s && typeof s === 'object' && 'columnKey' in s ? s.columnKey : undefined
      const order =
        s && typeof s === 'object' && 'order' in s
          ? (s.order as 'ascend' | 'descend' | null)
          : null
      if (
        order &&
        (columnKey === 'taskId' ||
          columnKey === 'paymentAssignmentId' ||
          columnKey === 'carrierName' ||
          columnKey === 'benefitId' ||
          columnKey === 'failedAt' ||
          columnKey === 'processTimeSec' ||
          columnKey === 'secondAttemptSuccess')
      ) {
        setTableSorter({ columnKey, order })
      } else {
        setTableSorter(null)
      }
      const bucket = filters.processTimeSec?.[0]
      setProcessTimeBucket(parseProcessTimeBucket(bucket))
      const secondAttempt = filters.secondAttemptSuccess?.[0]
      setSecondAttemptSuccess(
        secondAttempt === 'true'
          ? true
          : secondAttempt === 'false'
            ? false
            : null
      )
      const benefitId = filters.benefitId?.[0] as string | undefined
      setSelectedBenefitId(benefitId || null)
      setFailurePage(1)
    },
    []
  )

  const toggleErrorCode = useCallback((code: string) => {
    setSelectedErrorCode((prev) => (prev === code ? null : code))
    setFailurePage(1)
  }, [])

  const clearErrorCode = useCallback(() => {
    setSelectedErrorCode(null)
    setFailurePage(1)
  }, [])

  return {
    isAllCarriers,
    selectedErrorCode,
    selectedCarrierNames,
    setSelectedCarrierNames,
    processTimeBucket,
    setProcessTimeBucket,
    secondAttemptSuccess,
    setSecondAttemptSuccess,
    selectedBenefitId,
    setSelectedBenefitId,
    failurePage,
    setFailurePage,
    failurePageSize,
    setFailurePageSize,
    tableSorter,
    recentFailures,
    failuresTotal,
    failuresFetching,
    failuresIsError,
    failuresError,
    handleFailureTableChange,
    toggleErrorCode,
    clearErrorCode,
  }
}

export type CarrierFailuresController = ReturnType<typeof useCarrierFailures>
